import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  LogOut,
  Workflow,
  Building2,
  Sheet,
  ListTodo,
  Sun,
  Moon,
  Monitor,
  MessageSquare,
  Sparkles,
  CircleDollarSign,
  CalendarDays,
  LayoutGrid,
  Send,
  Server,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme, type Theme } from '@/lib/theme';

const nav = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/pipelines', icon: Workflow, label: 'Pipelines' },
  { to: '/accounts', icon: Building2, label: 'Kommo accounts' },
  { to: '/sheets', icon: Sheet, label: 'Sheets' },
  { to: '/events', icon: ListTodo, label: 'Events' },
  { to: '/last-note', icon: MessageSquare, label: 'Last Note' },
  { to: '/ai-summary', icon: Sparkles, label: 'AI Summary' },
  { to: '/gcal', icon: CalendarDays, label: 'Google Calendar' },
  { to: '/widgets', icon: LayoutGrid, label: 'Виджеты' },
  { to: '/meta-leads', icon: Send, label: 'Meta-заявки' },
  { to: '/hosting', icon: Server, label: 'Хостинг' },
  { to: '/billing', icon: CircleDollarSign, label: 'Биллинг' },
];

export function Layout(): JSX.Element {
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: me } = useQuery<{ user: { name: string } }>({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('not_authenticated');
      return res.json();
    },
    retry: false,
    staleTime: 60_000,
  });

  async function onLogout(): Promise<void> {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen flex">
      <aside className="flex w-60 shrink-0 flex-col border-r border-border bg-card/40 px-4 py-6">
        <div className="mb-8 flex items-center gap-3 px-2">
          <img
            src="/logo.svg"
            alt="Bridge"
            className="h-9 w-9 rounded-md border border-border"
          />
          <div className="min-w-0">
            <div className="text-lg font-bold leading-tight tracking-tight">Bridge</div>
            <div className="truncate text-xs text-muted-foreground">Paradigma · Kommo → Sheets</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        {me?.user && (
          <button
            type="button"
            onClick={() => void onLogout()}
            className="mt-2 flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <span className="truncate">{me.user.name}</span>
            <LogOut className="h-3.5 w-3.5" />
          </button>
        )}
        <ThemeToggle theme={theme} setTheme={setTheme} />
      </aside>
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function ThemeToggle({ theme, setTheme }: { theme: Theme; setTheme: (t: Theme) => void }): JSX.Element {
  const options: Array<{ value: Theme; icon: typeof Sun; label: string }> = [
    { value: 'light', icon: Sun, label: 'Светлая' },
    { value: 'dark', icon: Moon, label: 'Тёмная' },
    { value: 'system', icon: Monitor, label: 'Система' },
  ];
  return (
    <div className="mt-4 rounded-md border border-border p-1">
      <div className="grid grid-cols-3 gap-0.5">
        {options.map((opt) => {
          const Icon = opt.icon;
          const active = theme === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setTheme(opt.value)}
              title={opt.label}
              className={cn(
                'flex items-center justify-center rounded-sm py-1.5 transition-colors',
                active
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent/50',
              )}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
